/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable no-console */
import { useEffect, useRef, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { FeatureFlagContext } from '../../../context/FeatureFlagContext';
import { AgreementContext } from '../../../context/AgreementContext';
import { GetCustomer, GetLegalHold } from '../../../api/user';
import { CustomerLoyaltyProgram } from '../../../utils/rewardUtils';

interface LSLocationState {
  customerId?: string;
}

interface Phone {
  phoneType: string;
  primary: string;
  phoneNumber: string;
  countryCode?: string;
}

/**
 * Hook that fetches customer data ONCE at the LocalSourcingRoute level.
 * Stores the response in AgreementContext so all child steps
 * (Agreement Details, PO Status, Delivery) can reuse it without re-fetching.
 *
 * Returns the persisted customerId from location.state.
 */
export function useLocalSourcingCustomer(): string {
  const location = useLocation<LSLocationState>();
  const { featureFlagDetails } = useContext(FeatureFlagContext);
  const {
    setentireGetCustomerData,
    setGlobalcusId,
    getCustomerResponse,
    setGetCustomerResponse,
    CustomerInfoContext,
    setCustomerTierDetails,
    setCustomerPromotions,
    setCustomerTierCodes,
  } = useContext(AgreementContext);
  const [, setCustomerValue] = CustomerInfoContext;

  // Persist customerId from location.state across sub-step navigations
  const customerIdRef = useRef<string>(location.state?.customerId || '');
  const customerId = customerIdRef.current;

  const customerFetched = useRef(false);

  useEffect(() => {
    if (!customerId || customerFetched.current || getCustomerResponse) return;
    customerFetched.current = true;

    const fetchCustomerData = async () => {
      const currentStore = sessionStorage.getItem('storeNumber');
      try {
        const [, response] = await Promise.all([
          GetLegalHold(customerId),
          GetCustomer(customerId, currentStore),
        ]);

        console.log('[LS Route] Customer data fetched:', response?.status);

        if (response?.status !== 200) return;

        const customerData = response.data.GetCustomer.value;
        setentireGetCustomerData(customerData);
        setGlobalcusId(customerData.globalCustomerId);
        setGetCustomerResponse(response.data);

        const phones: Phone[] = customerData.phones || [];
        const cellPhone = phones.find(
          (p) => p.phoneType === 'CELL' && p.primary === 'Y'
        );

        setCustomerValue({
          GlobalCustomerID: customerData.globalCustomerId,
          CustomerName: `${customerData.firstName} ${customerData.lastName}`,
          CustomerId: customerData.customerId,
          CusotmerPhoneNumber: cellPhone?.phoneNumber || '',
          countryCode: cellPhone?.countryCode || '',
          CustomerEmail: customerData.emailAddress,
          CoCustomerName: '',
          CoCustomerId: customerData.coCustomerId || '',
          Skip: customerData.skip || '',
          Stolen: customerData.stolen || '',
          Hard: customerData.hard || '',
        });

        await fetchLoyaltyIfEnabled(customerData);
      } catch (error) {
        console.error('[LS Route] Error fetching customer data:', error);
      }
    };

    const fetchLoyaltyIfEnabled = async (
      customerData: Record<string, string>
    ) => {
      if (
        featureFlagDetails?.EnableCustomerTierReward !== '1' ||
        customerData.coworker === 'Y' ||
        !customerData.globalCustomerId
      ) {
        return;
      }
      try {
        const rewardResponse = await CustomerLoyaltyProgram(
          customerData.globalCustomerId
        );
        if (rewardResponse) {
          setCustomerTierDetails(rewardResponse.loyaltyProgram);
          setCustomerPromotions(rewardResponse.eligiblePromotions);
          setCustomerTierCodes(rewardResponse.customerTierCodes);
        }
      } catch (err) {
        console.error('[LS Route] Loyalty program error:', err);
      }
    };

    fetchCustomerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  return customerId;
}
